/**
 * Dar la novela por cerrada y pasar un texto por el detector externo, Pangram (Rais, 10-oct-2026).
 *
 * La misma pieza en dos sitios:
 *   · en Escritura (`deLaNovela`), debajo de «Construir la novela»: el autor la da por cerrada —lo decide él, no
 *     el cierre de la última escena— y entonces se le ofrece pasarla por el detector;
 *   · en Diagnóstico: el documento que haya abierto en Word, tal como está guardado (el manuscrito de un cliente).
 *
 * ES LA ÚNICA PIEZA DEL PANEL QUE MANDA TEXTO A ALGUIEN QUE NO ES EL MODELO, Y LA ÚNICA QUE COBRA. Por eso: al
 * abrirse solo mira (no gasta ni envía nada); el envío necesita DOS clics, y entre uno y otro se dice qué texto
 * sale, hacia quién, por dónde pasa y cuánto cuesta; y solo pregunta cómo va mientras hay un envío en marcha (o se
 * espera su pago). No habla con Word.
 *
 * La llave de Pangram es de HumanInk y no está en el ordenador del autor: quien quiere pasar un texto COMPRA el
 * paso de ese fichero (un pago único, en la página de Stripe que abre el motor). El panel no ve ni llave ni tarjeta.
 */
import { useCallback, useEffect, useState } from "react";
import * as api from "./guionApi";
import { fraseDeLoQueViaja, rotuloDePasar, rotuloDeConfirmar, fraseDeResultado, fraseDelAviso, capitulosConAviso, fraseDeHumanizadas, fraseDeAvance, fraseDeCerrar, fraseDeOmitidos, fraseDeNoMedidos, fraseDeNoSeVende, porQueNo, diaYHora, miles,
  esperandoElPago, sePuedeEnviar, FRASE_DE_DEJADO, FRASE_DE_YA_PAGADO, LETRA_PEQUENA_DE_PANGRAM, type EstadoDelDetector, type Zona } from "./detectorLogica";

/** Todo lo que la pieza le pide al motor. Va entero o no compila: una página de pruebas no puede acabar comprando en el motor de verdad. */
export type PuenteDelDetector = Pick<typeof api, "estadoDelDetector" | "cerrarLaNovela" | "reabrirLaNovela" | "pasarPorElDetector" | "comprarElDetector" | "dejarDeEsperarElPago">;
const DE_VERDAD: PuenteDelDetector = { estadoDelDetector: api.estadoDelDetector, cerrarLaNovela: api.cerrarLaNovela, reabrirLaNovela: api.reabrirLaNovela, pasarPorElDetector: api.pasarPorElDetector,
  comprarElDetector: api.comprarElDetector, dejarDeEsperarElPago: api.dejarDeEsperarElPago };

/** Cada cuánto se pregunta por el envío EN MARCHA. Sin envío en marcha no se pregunta nada. */
const CADA = 2500;
const COLOR: Record<Zona, string> = { verde: "var(--hi-verde)", ambar: "var(--hi-ambar)", rojo: "var(--hi-rojo)" };
const caja = { border: "1px solid var(--hi-raya)", borderRadius: 10, padding: "10px 12px", background: "var(--hi-papel)", fontSize: 13, lineHeight: 1.45, marginTop: 12 } as const;
const rotulo = { fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--hi-oficio)", marginBottom: 2 } as const;
const nota = { fontSize: 12, color: "var(--hi-tenue)", margin: "6px 0" } as const;
const boton = { background: "var(--hi-humano)", color: "var(--hi-sobre-acento)", fontWeight: 800, border: 0, borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer" } as const;
const enlace = { background: "none", border: 0, padding: 0, color: "var(--hi-humano)", fontSize: 12, cursor: "pointer", textDecoration: "underline" } as const;
const Punto = ({ zona }: { zona: Zona | null }) => (zona ? <span role="img" aria-label={`zona ${zona === "ambar" ? "amarilla" : zona === "rojo" ? "roja" : "verde"}`} style={{ display: "inline-block", width: 9, height: 9, borderRadius: 5, marginRight: 6, background: COLOR[zona] }} /> : null);

export default function Detector({ dir, doc, deLaNovela = false, refresco = "", puente = DE_VERDAD }: {
  dir: string; doc: string | null; deLaNovela?: boolean;
  /** Cambia cuando la novela se vuelve a construir: entonces se mira otra vez. */
  refresco?: string;
  puente?: PuenteDelDetector;
}) {
  const [e, setE] = useState<EstadoDelDetector | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /** El primer clic de cada cosa que necesita dos: cerrar la novela, o enviar el texto. */
  const [pedido, setPedido] = useState<"cerrar" | "enviar" | null>(null);
  /** Al pulsar «pagar» resultó que ya estaba pagado: se dice, porque el autor esperaba ver una página de pago. */
  const [yaPagado, setYaPagado] = useState(false);

  const mirar = useCallback(async () => {
    if (!dir) return;
    try { setE(await puente.estadoDelDetector(dir, doc, deLaNovela)); setFallo(null); }
    catch (x) { setFallo(x instanceof Error ? x.message : String(x)); }
  }, [dir, doc, deLaNovela, puente]);
  useEffect(() => { void mirar(); }, [mirar, refresco]);

  // Solo mientras hay un envío en marcha: se pregunta cómo va, y se deja de preguntar en cuanto acaba.
  const trabajando = e?.trabajo?.estado === "en_curso";
  useEffect(() => {
    if (!trabajando) return;
    const reloj = setTimeout(() => void mirar(), CADA);
    return () => clearTimeout(reloj);
  }, [trabajando, e, mirar]);

  const hacer = async (que: () => Promise<unknown>) => {
    if (ocupado) return;
    setOcupado(true); setFallo(null); setYaPagado(false);
    try { const r = await que(); if (r && typeof r === "object" && "hay" in r) setE(r as EstadoDelDetector); else await mirar(); }
    // Si algo falla se mira otra vez antes de decirlo: un precio que ha cambiado tiene que verse ya con su cifra nueva.
    catch (x) { await mirar(); setFallo(x instanceof Error ? x.message : String(x)); }
    finally { setOcupado(false); setPedido(null); }
  };
  const cerrar = () => hacer(() => puente.cerrarLaNovela(dir, doc));
  const reabrir = () => hacer(() => puente.reabrirLaNovela(dir, doc));
  /**
   * El SEGUNDO clic: aquí, y solo aquí, se pide el envío. Con la llave de este ordenador va directo; sin ella se
   * compra el paso, y al motor se le dice la cifra que el autor acaba de leer.
   */
  const enviar = () => hacer(async () => {
    if (e?.llave) await puente.pasarPorElDetector(dir, doc, deLaNovela);
    else setYaPagado((await puente.comprarElDetector(dir, doc, deLaNovela, e?.venta?.importe_cent ?? 0))?.ya_pagado === true);
  });
  /** Dejar de esperar el pago: no envía ni cobra nada. */
  const dejar = () => hacer(() => puente.dejarDeEsperarElPago(dir, doc, deLaNovela));

  if (!e) return fallo ? <div style={{ ...caja, color: "var(--hi-mal)" }}>{fallo}</div> : null;
  // La novela todavía no está construida: aquí no hay nada que decir (se construye justo encima).
  if (deLaNovela && (!e.hay || !e.novela)) return e.motivo === "varios_libros" ? <div style={caja}>{porQueNo(e)}</div> : null;

  const r = e.medida?.resumen ?? null, entera = !!e.medida?.entera;
  const seOfrece = !deLaNovela || !!e.cierre?.al_dia;
  const errorDelEnvio = e.trabajo?.estado === "fallido" && !entera ? e.trabajo.error : null;

  const elDetector = (
    <>
      {!e.hay && <p style={nota}>{porQueNo(e)}</p>}
      {e.hay && seOfrece && (<>
        {r && (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontSize: 13 }}><Punto zona={r.zona} /><b>Pangram: {fraseDeResultado(r)}</b></div>
            <div style={{ fontSize: 11, color: "var(--hi-tenue)", marginTop: 2 }}>
              {entera ? `Medido «${e.medida!.fichero}» entero (${miles(r.palabras)} palabras)${diaYHora(e.medida!.terminada) ? ` el ${diaYHora(e.medida!.terminada)}` : ""}.` : `Medidos ${miles(e.medida!.hechos)} de ${miles(e.medida!.bloques)} bloques: la cifra es de lo que lleva.`}
            </div>
            {fraseDelAviso(r) && <div style={{ fontSize: 12, color: "var(--hi-aviso)", marginTop: 4 }}>{fraseDelAviso(r)}</div>}
            {capitulosConAviso(r).length > 0 && (
              <ul style={{ margin: "4px 0 0", paddingLeft: 16, fontSize: 12 }}>
                {capitulosConAviso(r).slice(0, 8).map((c) => <li key={c.titulo} style={{ overflowWrap: "anywhere" }}><Punto zona={c.zona} />{c.titulo}: {c.frase}</li>)}
              </ul>
            )}
            {fraseDeHumanizadas(r) && <div style={{ fontSize: 12, color: "var(--hi-aviso)", marginTop: 4 }}>{fraseDeHumanizadas(r)}</div>}
            {fraseDeOmitidos(e.medida) && <div style={{ fontSize: 11, color: "var(--hi-tenue)", marginTop: 4 }}>{fraseDeOmitidos(e.medida)}</div>}
            {fraseDeNoMedidos(e.medida, !e.llave) && <div style={{ fontSize: 11, color: "var(--hi-tenue)", marginTop: 4 }}>{fraseDeNoMedidos(e.medida, !e.llave)}</div>}
          </div>
        )}
        {trabajando && <div role="status" style={{ fontSize: 12, color: "var(--hi-aviso)", marginTop: 6 }}>{fraseDeAvance(e.trabajo)}</div>}
        {trabajando && esperandoElPago(e.trabajo) && (
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 6, fontSize: 12 }}>
            {e.pedido?.url && <a href={e.pedido.url} target="_blank" rel="noopener" style={{ color: "var(--hi-humano)" }}>¿No se ha abierto? Abrir la página de pago</a>}
            <button onClick={() => void dejar()} disabled={ocupado} style={enlace}>Dejar de esperar</button>
          </div>
        )}
        {yaPagado && <p style={nota}>{FRASE_DE_YA_PAGADO}</p>}
        {errorDelEnvio && <div role="alert" style={{ fontSize: 12, color: "var(--hi-mal)", marginTop: 6 }}>{errorDelEnvio}</div>}
        {!trabajando && e.trabajo?.dejado && !entera && <p style={nota}>{FRASE_DE_DEJADO}</p>}
        {!trabajando && fraseDeNoSeVende(e) && <p style={nota}>{fraseDeNoSeVende(e)}</p>}
        {!trabajando && sePuedeEnviar(e) && e.viaja && e.viaja.faltan > 0 && (pedido === "enviar" ? (
          <div style={{ marginTop: 8, borderTop: "1px solid var(--hi-raya)", paddingTop: 8 }}>
            <div style={{ fontSize: 12 }}>{fraseDeLoQueViaja(e)}</div>
            {fraseDeOmitidos(e.viaja) && <div style={{ fontSize: 11, color: "var(--hi-tenue)", marginTop: 4 }}>{fraseDeOmitidos(e.viaja)}</div>}
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
              <button onClick={() => void enviar()} disabled={ocupado} style={boton}>{rotuloDeConfirmar(e)}</button>
              <button onClick={() => setPedido(null)} style={enlace}>Cancelar</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setPedido("enviar")} disabled={ocupado} style={{ ...boton, marginTop: 8 }}>{rotuloDePasar(e)}</button>
        ))}
        {!trabajando && !e.viaja && !r && <p style={nota}>No he podido leer el documento para decirte cuánto costaría. Ciérralo en Word y vuelve a esta pestaña.</p>}
        <div style={{ fontSize: 11, color: "var(--hi-apagado)", marginTop: 8 }}>{LETRA_PEQUENA_DE_PANGRAM}</div>
      </>)}
    </>
  );

  if (!deLaNovela) return (
    <div style={caja}>
      <div style={rotulo}>Detector externo · Pangram</div>
      <div style={{ fontSize: 12 }}>Pasa este documento, tal como está guardado, por Pangram, un detector de IA muy usado. Sirve para saber de antemano qué vería una editorial o una agencia que pase tu texto por un detector. No es una prueba: un detector puede equivocarse.</div>
      {elDetector}
      {fallo && <div role="alert" style={{ fontSize: 12, color: "var(--hi-mal)", marginTop: 6 }}>{fallo}</div>}
    </div>
  );

  const c = e.cierre ?? null, marcas = e.marcas ?? 0;
  return (
    <div style={caja}>
      <div style={rotulo}>La novela, cerrada</div>
      {!c && (pedido === "cerrar" ? (
        <>
          <div style={{ fontSize: 12 }}>{fraseDeCerrar(e)}</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
            <button onClick={() => void cerrar()} disabled={ocupado} style={boton}>Sí, la novela está cerrada</button>
            <button onClick={() => setPedido(null)} style={enlace}>Cancelar</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12 }}>Cuando hayas terminado, dala por cerrada. Lo decides tú: no pasa sola al cerrar la última escena. Con la novela cerrada podrás pasarla por el detector externo (Pangram).</div>
          {marcas > 0
            ? <p style={{ ...nota, color: "var(--hi-aviso)" }}>«{e.novela!.fichero}» tiene {marcas === 1 ? "un cambio" : `${miles(marcas)} cambios`} sin aceptar ni rechazar. Decide en Word, guarda y vuelve: se cierra un texto decidido.</p>
            : <button onClick={() => setPedido("cerrar")} disabled={ocupado || !e.novela!.existe} style={{ ...boton, marginTop: 8 }}>Dar la novela por cerrada</button>}
        </>
      ))}
      {c && (
        <>
          <div style={{ fontSize: 12 }}>
            <b style={{ color: "var(--hi-verde)" }}>✓ Novela cerrada</b>{diaYHora(c.cuando) ? ` el ${diaYHora(c.cuando)}` : ""}, con «{c.fichero}».{" "}
            <button onClick={() => void reabrir()} disabled={ocupado || trabajando} style={enlace}>Reabrir</button>
          </div>
          {!c.al_dia && (
            <div style={{ fontSize: 12, color: "var(--hi-aviso)", marginTop: 6 }}>
              La novela ha cambiado desde que la cerraste{c.fichero_de_ahora && c.fichero_de_ahora !== c.fichero ? `: ahora es «${c.fichero_de_ahora}»` : ""}. Reábrela y ciérrala otra vez con el texto de ahora para pasarla por el detector.
            </div>
          )}
          {c.al_dia && <div style={{ ...rotulo, marginTop: 10 }}>Detector externo · Pangram</div>}
          {elDetector}
        </>
      )}
      {fallo && <div role="alert" style={{ fontSize: 12, color: "var(--hi-mal)", marginTop: 6 }}>{fallo}</div>}
    </div>
  );
}
